import * as v from 'valibot'

export type EntityListSearch = {
	skip?: number
	take?: number
	sortColumn?: string
	sortDirection?: string | number
	filters?: Record<string, string>
	section?: string
}

export type EntityTabSearch = EntityListSearch & {
	assignableSkip?: number
	assignableTake?: number
	assignableSortColumn?: string
	assignableSortDirection?: string | number
	assignableFilters?: Record<string, string>
	assignableFilterKeys?: string[]
	assignedSkip?: number
	assignedTake?: number
	assignedSortColumn?: string
	assignedSortDirection?: string | number
	assignedFilters?: Record<string, string>
	assignedFilterKeys?: string[]
}

type RelationSearchPrefix = 'assignable' | 'assigned'

export function getRelationApiSortColumn(sortColumn: string): string {
	const match = /^([a-zA-Z0-9]+)Name$/.exec(sortColumn)
	return match ? `${match[1]}.name` : sortColumn
}

const finiteNumberSchema = v.pipe(
	v.union([v.number(), v.string()]),
	v.transform(Number),
	v.check((value) => Number.isFinite(value)),
)

const optionalNumberSchema = v.fallback(
	v.optional(finiteNumberSchema),
	undefined,
)
const optionalStringSchema = v.fallback(v.optional(v.string()), undefined)
const optionalSortDirectionSchema = v.fallback(
	v.optional(v.union([v.string(), v.number()])),
	undefined,
)
const optionalFiltersSchema = v.fallback(
	v.optional(v.objectWithRest({}, v.unknown())),
	undefined,
)
const optionalStringArraySchema = v.pipe(
	v.fallback(v.optional(v.array(v.unknown())), undefined),
	v.transform((value) =>
		value?.filter((item): item is string => typeof item === 'string'),
	),
)

const relationSearchEntries = {
	assignableSkip: optionalNumberSchema,
	assignableTake: optionalNumberSchema,
	assignableSortColumn: optionalStringSchema,
	assignableSortDirection: optionalSortDirectionSchema,
	assignableFilters: optionalFiltersSchema,
	assignableFilterKeys: optionalStringArraySchema,
	assignedSkip: optionalNumberSchema,
	assignedTake: optionalNumberSchema,
	assignedSortColumn: optionalStringSchema,
	assignedSortDirection: optionalSortDirectionSchema,
	assignedFilters: optionalFiltersSchema,
	assignedFilterKeys: optionalStringArraySchema,
}

const optionalEntitySearchEntries = {
	skip: optionalNumberSchema,
	take: optionalNumberSchema,
	sortColumn: optionalStringSchema,
	sortDirection: optionalSortDirectionSchema,
	filters: optionalFiltersSchema,
	section: optionalStringSchema,
}

const entitySearchSchema = v.object({
	skip: v.pipe(
		v.fallback(finiteNumberSchema, 0),
		v.transform((value) => Math.max(0, value)),
	),
	take: v.pipe(
		v.fallback(finiteNumberSchema, 50),
		v.transform((value) => Math.min(500, Math.max(1, value))),
	),
	sortColumn: optionalStringSchema,
	sortDirection: optionalStringSchema,
	filters: optionalFiltersSchema,
	section: optionalStringSchema,
})

const entityTabSearchSchema = v.object({
	...entitySearchSchema.entries,
	...relationSearchEntries,
})

const optionalEntityTabSearchSchema = v.object({
	...optionalEntitySearchEntries,
	...relationSearchEntries,
})

export function validateEntitySearch(
	search: Record<string, unknown>,
): EntityListSearch {
	return v.parse(entitySearchSchema, search) as EntityListSearch
}

export function validateEntityTabSearch(
	search: Record<string, unknown>,
): EntityTabSearch {
	return v.parse(entityTabSearchSchema, search) as EntityTabSearch
}

export function validateEntityTabSearchOptional(
	search: Record<string, unknown>,
): EntityTabSearch {
	return v.parse(optionalEntityTabSearchSchema, search) as EntityTabSearch
}

export function getRelationSearch(
	search: EntityTabSearch,
	prefix: RelationSearchPrefix,
): EntityListSearch {
	const relationSearch = search as EntityTabSearch & Record<string, unknown>
	const legacyPagination = relationSearch[`${prefix}Pagination`] as
		| { pageIndex?: number; pageSize?: number }
		| undefined
	const prefixedSkip = search[`${prefix}Skip` as keyof EntityTabSearch] as
		| number
		| undefined
	const prefixedTake = search[`${prefix}Take` as keyof EntityTabSearch] as
		| number
		| undefined
	const take = prefixedTake ?? legacyPagination?.pageSize ?? search.take ?? 50
	const skip =
		prefixedSkip ??
		(legacyPagination?.pageIndex === undefined
			? search.skip
			: legacyPagination.pageIndex * take) ??
		0
	const sortColumn = search[`${prefix}SortColumn` as keyof EntityTabSearch] as
		| string
		| undefined
	const sortDirection = search[
		`${prefix}SortDirection` as keyof EntityTabSearch
	] as string | number | undefined
	const filters = search[`${prefix}Filters` as keyof EntityTabSearch] as
		| Record<string, string>
		| undefined
	const resolvedSortColumn = sortColumn ?? search.sortColumn

	return {
		skip,
		take,
		sortColumn: getRelationApiSortColumn(resolvedSortColumn ?? ''),
		sortDirection: sortDirection ?? search.sortDirection ?? 0,
		filters: filters ?? search.filters ?? {},
		section: search.section,
	} satisfies EntityListSearch
}
